import { useRef, useState } from "react";

const SPORTS_INFO = {
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: "🏸", label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

const WEEK_DAYS = ["일", "월", "화", "수", "목", "금", "토"];

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" })
    + ` (${WEEK_DAYS[d.getDay()]})`;
}

function buildLocation(match) {
  return [match?.location_city, match?.location_district, match?.location_name]
    .filter(Boolean).join(" ") || null;
}

/* ── 종목별 상세 스코어 요약 (한 줄 텍스트) ── */
function DetailSummary({ sportsType, detailScores, hostTeamName, guestTeamName }) {
  if (!detailScores) return null;

  if (sportsType === "baseball") {
    const h = detailScores;
    if (!h?.host) return null;
    return (
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8, textAlign: "center" }}>
        {h.host.map((v, i) => `${i + 1}회: ${v}-${h.guest[i] ?? 0}`).join("  ")}
      </div>
    );
  }

  if (["tennis", "badminton", "tabletennis", "running"].includes(sportsType)) {
    const ds = detailScores;
    if (!Array.isArray(ds?.games)) return null;
    return (
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8, textAlign: "center" }}>
        {ds.games.map((g, gi) => {
          const sets = (g.sets || []).map(s => `${s.hs}:${s.gs}`).join(" ");
          return <span key={gi} style={{ marginRight: 8 }}>G{gi + 1}: {sets}</span>;
        })}
      </div>
    );
  }

  if (sportsType === "bowling" || sportsType === "golf") {
    if (!Array.isArray(detailScores)) return null;
    const hostPlayers  = detailScores.filter(p => p.team === "host");
    const guestPlayers = detailScores.filter(p => p.team === "guest");
    return (
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8, display: "flex", justifyContent: "center", gap: 16 }}>
        <span>{hostTeamName}: {hostPlayers.map(p => p.score).join(", ")}</span>
        <span>{guestTeamName}: {guestPlayers.map(p => p.score).join(", ")}</span>
      </div>
    );
  }

  if (sportsType === "billiards" && Array.isArray(detailScores)) {
    return (
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8, textAlign: "center" }}>
        {detailScores.map((m, i) => `${i + 1}대결 ${m.hs}:${m.gs}`).join("  ")}
      </div>
    );
  }

  return null;
}

/* ── 메인 컴포넌트 ── */
export default function ScoreCardImage({
  match,
  hostTeamName,
  guestTeamName,
  hostScore,
  guestScore,
  detailScores,
  sportsType,
  onClose,
}) {
  const cardRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const sport    = SPORTS_INFO[sportsType] || { icon: "🏅", label: sportsType || "경기" };
  const date     = formatDate(match?.game_date);
  const location = buildLocation(match);

  const isGolf   = sportsType === "golf";
  const hostWins = isGolf ? hostScore < guestScore : hostScore > guestScore;
  const guestWins = isGolf ? guestScore < hostScore : guestScore > hostScore;
  const isDraw   = hostScore === guestScore;

  const hostColor  = hostWins ? "#E8412A" : isDraw ? "#1A1A2E" : "#d1d5db";
  const guestColor = guestWins ? "#E8412A" : isDraw ? "#1A1A2E" : "#d1d5db";

  const resultText = isDraw
    ? "🤝 무승부"
    : hostWins
    ? `🏆 ${hostTeamName} 승리!`
    : `🏆 ${guestTeamName} 승리!`;

  const resultColor = isDraw ? "#d97706" : "#E8412A";

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        allowTaint: false,
      });
      const dataUrl = canvas.toDataURL("image/png");

      const { Capacitor } = await import("@capacitor/core");
      if (Capacitor.isNativePlatform()) {
        const { Filesystem, Directory } = await import("@capacitor/filesystem");
        const base64    = dataUrl.split(",")[1];
        const fileName  = `크루픽_교류전결과_${Date.now()}.png`;
        await Filesystem.writeFile({
          path:      fileName,
          data:      base64,
          directory: Directory.Documents,
        });
        alert("갤러리/문서 폴더에 저장되었습니다!");
      } else {
        const link    = document.createElement("a");
        link.download = `크루픽_교류전결과_${Date.now()}.png`;
        link.href     = dataUrl;
        link.click();
      }
    } catch (e) {
      alert("저장 중 오류가 발생했어요.");
      console.error(e);
    }
    setSaving(false);
  };

  return (
    <div style={s.backdrop} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>

        {/* 캡처 대상 카드 */}
        <div ref={cardRef} style={s.card}>
          {/* 헤더 */}
          <div style={s.cardHeader}>
            <span style={s.appLabel}>크루픽 교류전 결과</span>
            <span style={{ fontSize: 22 }}>{sport.icon}</span>
          </div>
          <div style={s.sportLabel}>{sport.label}</div>

          {/* 날짜 / 장소 */}
          {(date || location) && (
            <div style={s.meta}>
              {date     && <span>📅 {date}</span>}
              {location && <span>📍 {location}</span>}
            </div>
          )}

          {/* 구분선 */}
          <div style={s.divider} />

          {/* 스코어 메인 */}
          <div style={s.scoreRow}>
            <div style={s.teamBlock}>
              <div style={s.teamName}>{hostTeamName}</div>
              <div style={{ ...s.scoreNum, color: hostColor }}>{hostScore}</div>
            </div>
            <div style={s.colon}>:</div>
            <div style={{ ...s.teamBlock, textAlign: "right" }}>
              <div style={s.teamName}>{guestTeamName}</div>
              <div style={{ ...s.scoreNum, color: guestColor }}>{guestScore}</div>
            </div>
          </div>

          {/* 결과 텍스트 */}
          <div style={{ ...s.resultBadge, color: resultColor }}>
            {resultText}
          </div>

          {/* 상세 스코어 */}
          <DetailSummary
            sportsType={sportsType}
            detailScores={detailScores}
            hostTeamName={hostTeamName}
            guestTeamName={guestTeamName}
          />

          {/* 워터마크 */}
          <div style={s.watermark}>crewpick.app</div>
        </div>

        {/* 액션 버튼 */}
        <div style={s.btnRow}>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? "저장 중..." : "📥 이미지 저장"}
          </button>
          <button style={s.closeBtn} onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  backdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 600, padding: "0 20px",
  },
  modal: {
    width: "100%", maxWidth: 360,
    display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
    fontFamily: "'Noto Sans KR', sans-serif",
  },

  /* 카드 (html2canvas 캡처 대상) */
  card: {
    width: "100%", background: "#fff",
    borderRadius: 20, padding: "24px 24px 20px",
    boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
    boxSizing: "border-box",
  },
  cardHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2,
  },
  appLabel: {
    fontSize: 11, fontWeight: 700, color: "#E8412A", letterSpacing: 0.5,
  },
  sportLabel: {
    fontSize: 13, fontWeight: 600, color: "#9ca3af", marginBottom: 10,
  },
  meta: {
    display: "flex", flexDirection: "column", gap: 3, marginBottom: 12,
  },
  divider: {
    height: 1, background: "#f3f4f6", margin: "12px 0",
  },

  /* 스코어 */
  scoreRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 12,
  },
  teamBlock: {
    flex: 1, textAlign: "left",
  },
  teamName: {
    fontSize: 13, fontWeight: 700, color: "#374151",
    marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  scoreNum: {
    fontSize: 52, fontWeight: 900, lineHeight: 1, fontVariantNumeric: "tabular-nums",
  },
  colon: {
    fontSize: 32, fontWeight: 900, color: "#e5e7eb",
    margin: "0 12px", alignSelf: "flex-end", paddingBottom: 6,
  },
  resultBadge: {
    textAlign: "center", fontSize: 15, fontWeight: 800, marginBottom: 4,
  },

  /* 워터마크 */
  watermark: {
    textAlign: "center", fontSize: 11, color: "#d1d5db",
    marginTop: 14, letterSpacing: 1,
  },

  /* 버튼 */
  btnRow: {
    display: "flex", gap: 10, width: "100%",
  },
  saveBtn: {
    flex: 1, padding: "13px", background: "#E8412A", color: "#fff",
    border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit",
  },
  closeBtn: {
    padding: "13px 20px", background: "#f5f6fa", color: "#374151",
    border: "1px solid #e5e7eb", borderRadius: 12, fontSize: 14,
    fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
  },
};
